import { useMemo } from "react";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useNrtUserPermissionGroups } from "@dealer-center/ddc-pages-nrt-database-filter-tool-fetch";
import {
  NRT_NO_PERMISSIONS,
  deriveNrtPermissions
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import type { NrtRecordPermissions } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";

export interface UseNrtPermissionsResult {
  permissions: NrtRecordPermissions;
  // Stamped onto new records and compared against each record's creator.
  currentUserId: string | null;
  // Safe state is read-only so an affordance is never offered then withdrawn (FR-016).
  isResolving: boolean;
  // Read-only, and the page must say so (FR-017) rather than treating it as "holds no roles".
  isIndeterminate: boolean;
}

// Composes the session with the groups lookup, which is now a dependent
// query — it needs the session's userId before it can fire.
export const useNrtPermissions = (): UseNrtPermissionsResult => {
  const { data: sessionUser, isLoading: isSessionLoading } = useGetUser();
  const userId = sessionUser?.user ?? null;
  const {
    data: groups,
    isLoading: isGroupsLoading,
    isError: isGroupsError
  } = useNrtUserPermissionGroups(userId);
  const isLoading = isSessionLoading || isGroupsLoading;
  const isIndeterminate = !isLoading && (isGroupsError || !userId);

  // Stable reference: rebuilding this on every render would rebuild the
  // records table's columns on every keystroke in the search box.
  const permissions = useMemo(() => {
    if (isLoading || isIndeterminate) {
      return NRT_NO_PERMISSIONS;
    }
    return deriveNrtPermissions(groups ?? [], userId);
  }, [groups, userId, isLoading, isIndeterminate]);

  return {
    permissions,
    currentUserId: userId,
    isResolving: isLoading,
    isIndeterminate
  };
};
